import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  getCode,
  getCodes,
  getSubcode,
  getSubcodes,
  createCode,
  createSubcode,
  updateCode,
  updateSubcode,
  updateSubcodeSeq,
} from '../api/codes.api';
import {
  ICode,
  ISubcode,
  IGetCode,
  IGetCodes,
  IGetSubcode,
  IGetSubcodes,
  ICodeReq,
  ICodeSeqReq,
} from '../models/codes.model';

interface ICodeState {
  code?: ICode;
  codes?: ICode[];
  subcode?: ISubcode;
  subcodes?: ISubcode[];
  isLoading: boolean;
  error?: AxiosError | string;
}

const initialState: ICodeState = {
  code: undefined,
  codes: [],
  isLoading: false,
};

const codeSlicer = createSlice({
  name: 'code',
  initialState,
  reducers: {
    getCodeStart: (state: ICodeState) => {
      state.code = undefined;
      state.isLoading = true;
      state.error = undefined;
    },
    getCodeSuccess: (state: ICodeState, action: PayloadAction<IGetCode>) => {
      state.code = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getCodeFailure: (
      state: ICodeState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.code = undefined;
      state.isLoading = false;
      state.error = action.payload;
    },
    getCodesStart: (state: ICodeState) => {
      state.codes = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getCodesSuccess: (state: ICodeState, action: PayloadAction<IGetCodes>) => {
      state.codes = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getCodesFailure: (
      state: ICodeState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.codes = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    getSubcodeStart: (state: ICodeState) => {
      state.subcode = undefined;
      state.isLoading = true;
      state.error = undefined;
    },
    getSubcodeSuccess: (
      state: ICodeState,
      action: PayloadAction<IGetSubcode>,
    ) => {
      state.subcode = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getSubcodeFailure: (
      state: ICodeState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.subcode = undefined;
      state.isLoading = false;
      state.error = action.payload;
    },
    getSubcodesStart: (state: ICodeState) => {
      state.subcodes = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getSubcodesSuccess: (
      state: ICodeState,
      action: PayloadAction<IGetSubcodes>,
    ) => {
      state.subcodes = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getSubcodesFailure: (
      state: ICodeState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.subcodes = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    requestStart: (state: ICodeState) => {
      state.isLoading = true;
      state.error = undefined;
    },
    requestFailure: (
      state: ICodeState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    createSuccess: (state: ICodeState) => {
      state.isLoading = false;
      state.error = undefined;
    },
    updateSuccess: (state: ICodeState) => {
      state.isLoading = false;
      state.error = undefined;
    },
  },
});

export const {
  getCodeStart,
  getCodeSuccess,
  getCodeFailure,
  getCodesStart,
  getCodesSuccess,
  getCodesFailure,
  getSubcodeStart,
  getSubcodeSuccess,
  getSubcodeFailure,
  getSubcodesStart,
  getSubcodesSuccess,
  getSubcodesFailure,
  requestStart,
  requestFailure,
  createSuccess,
  updateSuccess,
} = codeSlicer.actions;

export default codeSlicer.reducer;

export const doGetCode =
  (id: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(getCodeStart());
      const response = await getCode(id);
      dispatch(getCodeSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getCodeFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doGetCodes = (): any => async (dispatch: any) => {
  try {
    dispatch(getCodesStart());
    const response = await getCodes();
    dispatch(getCodesSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(getCodesFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doGetSubcode =
  (code: string, id: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(getSubcodeStart());
      const response = await getSubcode(code, id);
      dispatch(getSubcodeSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getSubcodeFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doGetSubcodes =
  (code: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(getSubcodesStart());
      const response = await getSubcodes(code);
      dispatch(getSubcodesSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getSubcodesFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doCreateCode =
  (data: ICodeReq): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await createCode(data);
      dispatch(createSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doCreateSubcode =
  (code: string, data: ISubcode): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await createSubcode(code, data);
      dispatch(createSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doUpdateCode =
  (id: string, data: ICodeReq): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await updateCode(id, data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doUpdateSubcode =
  (code: string, id: string, data: ISubcode): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await updateSubcode(code, id, data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doUpdateSubcodeSeq =
  (id: string, data: ICodeSeqReq[]): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await updateSubcodeSeq(id, data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };
